import { useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import {
  Box,
  Button,
  Card,
  CardContent,
  CircularProgress,
  Stack,
  TextField,
  Typography
} from "@mui/material";

import { useLogin } from "../api/hooks";
import { useAuthStore } from "../store/authStore";
import { useNotificationStore } from "../store/notificationStore";
import { initKeycloakClient } from "../auth/keycloakClient";

interface LocationState {
  from?: { pathname: string };
}

export function Login() {
  const navigate = useNavigate();
  const location = useLocation();
  const login = useLogin();
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");

  const authRequired = useAuthStore((state) => state.authRequired);
  const provider = useAuthStore((state) => state.provider);
  const authLoading = useAuthStore((state) => state.authLoading);
  const oidcIssuer = useAuthStore((state) => state.oidcIssuer);
  const oidcClientId = useAuthStore((state) => state.oidcClientId);
  const isAuthenticated = useAuthStore((state) => state.isAuthenticated());
  const setAuth = useAuthStore((state) => state.setAuth);
  const logoutVersion = useAuthStore((state) => state.logoutVersion);
  const lastLogoutReason = useAuthStore((state) => state.lastLogoutReason);
  const enqueueNotification = useNotificationStore((state) => state.enqueue);

  const from = useMemo(() => {
    const state = location.state as LocationState | null;
    return state?.from?.pathname ?? "/";
  }, [location.state]);

  useEffect(() => {
    if (authRequired === false) {
      navigate(from, { replace: true });
    }
  }, [authRequired, from, navigate]);

  useEffect(() => {
    if (logoutVersion === 0) {
      return;
    }
    if (lastLogoutReason === "unauthorized") {
      enqueueNotification({
        message: "Sessione scaduta, effettua nuovamente l'accesso.",
        severity: "warning"
      });
    }
  }, [enqueueNotification, lastLogoutReason, logoutVersion]);

  useEffect(() => {
    if (authRequired && isAuthenticated) {
      navigate(from, { replace: true });
    }
  }, [authRequired, from, isAuthenticated, navigate]);

  useEffect(() => {
    if (provider === "keycloak" && authRequired && oidcIssuer && oidcClientId) {
      void initKeycloakClient({ issuer: oidcIssuer, clientId: oidcClientId });
    }
  }, [authRequired, oidcClientId, oidcIssuer, provider]);

  if (authRequired === null) {
    return (
      <Box sx={{ display: "flex", alignItems: "center", justifyContent: "center", minHeight: "100vh" }}>
        <CircularProgress />
      </Box>
    );
  }

  if (authRequired === false) {
    return null;
  }

  if (provider === "keycloak") {
    return (
      <Box
        sx={{
          minHeight: "100vh",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          backgroundColor: (theme) => theme.palette.background.default
        }}
      >
        <Stack spacing={2} alignItems="center">
          <CircularProgress />
          <Typography variant="body1" color="text.secondary">
            Reindirizzamento a Keycloak{authLoading ? "…" : "."}
          </Typography>
        </Stack>
      </Box>
    );
  }

  const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    login.mutate(
      { username, password },
      {
        onSuccess: (data) => {
          setAuth(data.access_token, data.expires_at, data.username);
          enqueueNotification({
            message: "Accesso effettuato con successo.",
            severity: "success"
          });
          navigate(from, { replace: true });
        },
        onError: () => {
          enqueueNotification({
            message: "Credenziali non valide.",
            severity: "error"
          });
        }
      }
    );
  };

  return (
    <Box
      sx={{
        minHeight: "100vh",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        backgroundColor: (theme) => theme.palette.background.default,
        p: 2
      }}
    >
      <Card sx={{ maxWidth: 420, width: "100%" }}>
        <CardContent>
          <Typography variant="h5" component="h1" gutterBottom align="center">
            PEC Archiver Console
          </Typography>
          <Typography variant="body2" color="text.secondary" align="center" sx={{ mb: 3 }}>
            Inserisci le credenziali per accedere al pannello di controllo.
          </Typography>
          <Box component="form" onSubmit={handleSubmit}>
            <Stack spacing={2}>
              <TextField
                label="Utente"
                value={username}
                onChange={(event) => setUsername(event.target.value)}
                required
                autoFocus
                autoComplete="username"
              />
              <TextField
                label="Password"
                type="password"
                value={password}
                onChange={(event) => setPassword(event.target.value)}
                required
                autoComplete="current-password"
              />
              <Button type="submit" variant="contained" size="large" disabled={login.isPending}>
                {login.isPending ? <CircularProgress size={24} /> : "Accedi"}
              </Button>
            </Stack>
          </Box>
        </CardContent>
      </Card>
    </Box>
  );
}
